import { format, parse, startOfYear, setHours, startOfMonth } from 'date-fns'
import { es } from 'date-fns/locale'
import { toZonedTime, fromZonedTime } from 'date-fns-tz'
import { WeekDayEnum } from '../../../types/enums'

// Validates if string is of format YYYY-MM-DD
export function isValidDate(input: string) {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/

  if (!dateRegex.test(input)) {
    return false
  }
  const parts = input.split('-')
  const year = parseInt(parts[0] ?? '', 10)
  const month = parseInt(parts[1] ?? '', 10)
  const day = parseInt(parts[2] ?? '', 10)

  const date = new Date(year, month - 1, day)

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  )
}

export function getFirstDayOfCurrentMonth(monthDate?: string | Date | null) {
  const date = monthDate ? new Date(monthDate) : new Date()
  const startOfMonthDate = startOfMonth(date)
  const startOfMonthDateWithHour00 = setHours(startOfMonthDate, 0)

  return startOfMonthDateWithHour00 as Date
}

export function getFirstDayOfCurrentYear() {
  return startOfYear(new Date())
}

export function dateToDataTable(dateString?: string | Date | null) {
  if (!dateString) return null
  return format(new Date(dateString), 'dd/MM/yyyy')
}

// UTC version to prevent timezone shifts
export function dateToDataTableUTC(dateString?: string | Date | null) {
  if (!dateString) return null
  const date = new Date(dateString)
  const day = String(date.getUTCDate()).padStart(2, '0')
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const year = date.getUTCFullYear()
  const formatted = `${day}/${month}/${year}`
  return formatted
}

export function dateToDateTime(dateString?: string | Date | null) {
  if (!dateString) return null
  return format(new Date(dateString), 'dd/MM/yyyy HH:mm')
}

// dd/MM/yyyy => Date
export function dataTableToDate(dateString?: string | null) {
  if (!dateString) return null
  return parse(dateString, 'dd/MM/yyyy', new Date())
}

// 31 de agosto 2023
export function dateToFriendly(dateString: string | Date, timeZone?: string) {
  const date = timeZone
    ? toZonedTime(dateString, timeZone)
    : new Date(dateString)
  return format(date, 'dd MMMM yyyy', { locale: es }).replace(' ', ' de ')
}

export function dateToFriendlyDay(dateString: string | Date) {
  const date = new Date(dateString)
  return format(date, 'dd', { locale: es })
}

export function dateToFriendlyYear(dateString: string | Date) {
  const date = new Date(dateString)
  return format(date, 'yyyy', { locale: es })
}

export function dateToFriendlyAbrev(dateString: string | Date) {
  const date = new Date(dateString)
  return format(date, 'dd MMM', { locale: es })
}

export function dateToFriendlyMonthAbrev(dateString: string | Date) {
  const date = new Date(dateString)
  return format(date, 'MMM', { locale: es })
}

export function dateToInput(dateString?: string | Date, timeZone?: string) {
  if (!dateString) return undefined
  const date = timeZone
    ? toZonedTime(dateString, timeZone)
    : new Date(dateString)
  return format(date, 'yyyy-MM-dd')
}

// UTC date handling to fix timezone issues
// This function ensures dates are displayed consistently regardless of timezone
// Always uses UTC components to avoid timezone shifts when changing timezones
// When called with a Date, it extracts the date components the user sees (local date)
// and converts them to UTC midnight to ensure consistency
export function dateToInputUTC(dateString?: string | Date | null) {
  if (!dateString) return undefined

  if (typeof dateString === 'string') {
    // If it's already in YYYY-MM-DD format, use it directly
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString
    }
    // Parse ISO string or other date format and use UTC components
    const date = new Date(dateString)
    const year = date.getUTCFullYear()
    const month = String(date.getUTCMonth() + 1).padStart(2, '0')
    const day = String(date.getUTCDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  if (dateString instanceof Date) {
    // Check if Date is at UTC midnight (from database or dateFromInputUTC)
    // Dates stored in database are at UTC midnight (00:00:00 UTC)
    // Dates created by dateFromInputUTC are also at UTC midnight
    // Dates from DatePicker selection are at local midnight (00:00:00 local time)
    const utcHours = dateString.getUTCHours()
    const utcMinutes = dateString.getUTCMinutes()
    const utcSeconds = dateString.getUTCSeconds()
    const utcMilliseconds = dateString.getUTCMilliseconds()
    const isUTCMidnight =
      utcHours === 0 &&
      utcMinutes === 0 &&
      utcSeconds === 0 &&
      utcMilliseconds === 0

    if (isUTCMidnight) {
      // Date is at UTC midnight: use UTC components to get the correct calendar date
      // This ensures "2025-05-04 00:00:00 UTC" always converts to "2025-05-04"
      // regardless of what timezone the user is viewing it in
      const year = dateString.getUTCFullYear()
      const month = String(dateString.getUTCMonth() + 1).padStart(2, '0')
      const day = String(dateString.getUTCDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    } else {
      // Date is NOT at UTC midnight: likely from DatePicker selection at local midnight
      // Use local date components to capture what the user actually sees/selected
      // Example: User in UTC+1 selects May 4th
      // - DatePicker creates: new Date(2025, 4, 4) = 2025-05-04T00:00:00+01:00
      // - In UTC: 2025-05-03T23:00:00Z (not UTC midnight)
      // - getUTCDate() would give 3 (wrong!)
      // - getDate() gives 4 (correct - what user selected)
      const year = dateString.getFullYear()
      const month = String(dateString.getMonth() + 1).padStart(2, '0')
      const day = String(dateString.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  }

  return undefined
}

export function dateFromInputUTC(dateString?: string | null): Date | null {
  if (!dateString) return null
  // Parse input date string (YYYY-MM-DD) and create Date at UTC midnight
  // This ensures the date represents the correct calendar date regardless of timezone
  // The date string represents a calendar date (e.g., "2025-05-04" = May 4th, 2025)
  // By creating it at UTC midnight, we ensure it always represents that specific date
  // The DatePicker will display it correctly using UTC components via dateToDataTableUTC
  const [year, month, day] = dateString.split('-').map(Number)
  if (!year || !month || !day) return null
  // Create Date at UTC midnight - this ensures "2025-05-04" always represents May 4th
  // regardless of what timezone the user is in
  return new Date(Date.UTC(year, month - 1, day))
}

export function dateToDisplayUTC(
  dateString?: string | Date | null,
): string | null {
  if (!dateString) return null
  const date = new Date(dateString)
  // Use UTC methods to display consistent dates
  const day = String(date.getUTCDate()).padStart(2, '0')
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const year = date.getUTCFullYear()
  return `${day}/${month}/${year}`
}

// 02:05 PM (hora local)
export function timeToFriendly(dateString: string | Date) {
  const date = new Date(dateString)
  return format(date, 'HH:mm', { locale: es }) + ' (hora local)'
}

export function timeAbrev(dateString: string | Date) {
  const date =
    typeof dateString === 'string' ? new Date(dateString) : dateString
  return format(date, 'HH:mm', { locale: es })
}

export function timeAbrevUTC(dateString: string | Date) {
  const date = new Date(dateString)
  const hours = date.getUTCHours()
  const minutes = date.getUTCMinutes()
  const hoursString = hours < 10 ? `0${hours}` : hours.toString()
  const minutesString = minutes < 10 ? `0${minutes}` : minutes.toString()

  return `${hoursString}:${minutesString}`
}

export function timeToFriendlyAbrev(dateString: string | Date) {
  const date = new Date(dateString)
  return format(date, 'HH:mm', { locale: es }) + ' hrs'
}

// time format 11:30
export function setTime(date: Date | string, time: string) {
  const [hours, minutes] = time.split(':')
  const newDate = new Date(date)
  newDate.setHours(parseInt(hours ?? '0'))
  newDate.setMinutes(parseInt(minutes ?? '0'))
  return newDate
}

export const camelToSnakeCase = (str: string) => {
  return str.replace(/[A-Z]/g, (letter, index) => {
    return index == 0 ? letter.toLowerCase() : '_' + letter.toLowerCase()
  })
}

export function formatDecimals(number: number, maxDecimalNumbers: number) {
  if (Number.isInteger(number)) {
    return number
  }
  return Number(number.toFixed(maxDecimalNumbers))
}

export const minutesToTimeType = (minutes: number) => {
  if (minutes >= 60 && minutes < 60 * 24 && minutes % 60 === 0) {
    return { time: minutes / 60, type: 'hours' }
  }
  if (minutes >= 60 * 24 && minutes % (60 * 24) === 0) {
    return { time: minutes / (60 * 24), type: 'days' }
  }
  return { time: minutes, type: 'minutes' }
}

export const timeTypeToMinutes = (
  time: number,
  type: 'hours' | 'days' | 'minutes',
) => {
  if (type === 'days') {
    return time * 60 * 24
  }
  if (type === 'hours') {
    return time * 60
  }
  return time
}

// minutes=120 => '2 hours'
export const minutesTimeToFriendly = (timeInMinutes: number) => {
  const { time, type } = minutesToTimeType(timeInMinutes)
  return `${time} ${type}`
}

// 03:30 => 210 (minutes)
export const dateToMinutes = (date: Date) => {
  return new Date(date).getHours() * 60 + new Date(date).getMinutes()
}

// minutes=120 => '02:00'
export function minutesToTime(timeInMinutes: number) {
  const hours = Math.floor(timeInMinutes / 60)
  const minutes = timeInMinutes - hours * 60

  let hoursString = String(hours)
  let minutesString = String(minutes)

  if (hours < 10) {
    hoursString = `0${hours}`
  }

  if (minutes < 10) {
    minutesString = `0${minutes}`
  }

  return `${hoursString}:${minutesString}`
}

export const hoursRegEx = /^(?:[01]\d|2[0-3]):[0-5]\d|24:00$/

// '02:00' => minutes=120
export function timeToMinutes(time: string) {
  if (!hoursRegEx.test(time)) {
    return
  }
  const [hours, minutes] = time.split(':').map(Number)

  // @ts-ignore
  return hours * 60 + minutes
}

// Lunes => 1
export function stringToDayOfWeek(dayOfWeekStr: string) {
  const daysOfWeek = Object.values(WeekDayEnum)

  if (daysOfWeek.find((dayName) => dayName === dayOfWeekStr)) {
    return daysOfWeek.indexOf(dayOfWeekStr)
  }

  return
}

export function dayIndexToString(dayIndex: number | undefined) {
  if (!dayIndex && dayIndex !== 0) return undefined
  const daysOfWeek = Object.values(WeekDayEnum)

  if (dayIndex > daysOfWeek.length - 1) {
    return undefined
  }

  return daysOfWeek[dayIndex]
}

export function yearMonthAndDayToDate({
  fullYear,
  month = 1,
  date = 1,
}: {
  fullYear: number | string
  month?: number | string
  date?: number | string
}) {
  const monthToString = month.toString().padStart(2, '0')
  const datehToString = date.toString().padStart(2, '0')

  const newDate = new Date(
    `${fullYear}-${monthToString}-${datehToString} 00:00`,
  )

  if (isNaN(newDate.getTime())) {
    return undefined
  }

  return newDate
}

export function nameToSlug(name: string | undefined | null) {
  if (!name) {
    return name
  }

  const nameWithoutAccents = replaceAccents(name.toLocaleLowerCase())
  const withoutSpecialCharacters = nameWithoutAccents.replace(
    /[?&/\\|{},]/g,
    '',
  )
  const nameWithoutMultipleBlanks = removeMultipleBlanks(
    withoutSpecialCharacters,
  )
  return nameWithoutMultipleBlanks.replaceAll(' ', '-')
}

function normalizeForCarSlug(text: string): string {
  if (!text) return ''
  const withoutAccents = replaceAccents(text.toLocaleLowerCase())
  const withoutSpecialCharacters = withoutAccents.replace(/[?&/\\|{},._]/g, '-')
  const withoutMultipleBlanks = removeMultipleBlanks(withoutSpecialCharacters)
  const trimmed = withoutMultipleBlanks.trim()
  const withHyphens = trimmed.replace(/\s+/g, '-')
  return withHyphens.replace(/-+/g, '-').replace(/^-|-$/g, '')
}

export function generateCarSlugFromData({
  brandName,
  model,
  description,
  color,
  matriculationYear,
  mileage,
}: {
  brandName: string
  model: string
  description: string
  color: string
  matriculationYear: number | string
  mileage: number
}): string {
  const brand = brandName || ''
  const modelStr = model || ''
  const descriptionStr = description || ''
  const colorStr = color || ''
  const yearStr = String(matriculationYear ?? '')
  const mileageStr = `${mileage}km`

  const slug = `${brand}-${modelStr}-${descriptionStr}-${colorStr}-${yearStr}-${mileageStr}`
  return normalizeForCarSlug(slug)
}

export function replaceAccents(text: string) {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

// 'Hello     world,  what  a   wonderfull day' => 'Hello world, what a wonderfull day'
export function removeMultipleBlanks(text: string) {
  return text.replace(/\s+/g, ' ')
}

// To be used backend when: CREATING DATES IN BACKEND FROM STRING.
// '10:30' in fronted Timezone, if converted into new Date UTC it becomes '10:30' in UTC, then taken in frontend => 10:30 + diff between UTC and Timezone
export function dateUTCFromZonedDate(date: Date, timeZone: string): Date {
  return fromZonedTime(date, timeZone)
}
// To be used in backend when: getting a date or day with timezone data
// '15 of January at 23:00' in UTC may be '16 of January at 00:00' in zoned time. If getting the day it would take it wrong
export function zonedDateFromUTC(date: Date, timeZone: string): Date {
  return toZonedTime(date, timeZone)
}

export function pageToOffsetPagination({
  pageNumber,
  pageSize,
}: {
  pageNumber: number
  pageSize: number
}) {
  return {
    offset: (pageNumber - 1) * pageSize,
    limit: pageSize,
  }
}

//Converts date string to 10 length order number
export function orderNumberConverter(dateString: string) {
  const date = new Date(dateString)
  const dateNumber = date.getTime()
  const toBase66 = (num: number) => {
    const chars =
      '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz_-=+'

    let result = ''
    while (num > 0) {
      result += chars[num % 66]
      num = Math.floor(num / 66)
    }
    return result
  }

  return toBase66(dateNumber)
}

export function generateUniqueCode() {
  const now = new Date()
  const YY = String(now.getFullYear()).slice(-2)
  const MM = String(now.getMonth() + 1).padStart(2, '0')
  const DD = String(now.getDate()).padStart(2, '0')
  const hh = String(now.getHours()).padStart(2, '0')
  const mm = String(now.getMinutes()).padStart(2, '0')
  const ss = String(now.getSeconds()).padStart(2, '0')
  const ms = String(now.getMilliseconds()).padStart(3, '0')

  const dateString = `${YY}${MM}${DD}${hh}${mm}${ss}${ms}`
  const base65 = convertToBase65(dateString)

  return `-${base65}`
}

function convertToBase65(dateString: string) {
  const dateNumber = parseInt(dateString, 10)

  const toBase65 = (num: number) => {
    const chars =
      '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz_-.'

    let result = ''
    while (num > 0) {
      result += chars[num % 65]
      num = Math.floor(num / 65)
    }
    return result
  }

  return toBase65(dateNumber)
}
